import {Button} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {TooltipDeprecated} from '@wandb/weave/components/TooltipDeprecated';
import React, {FunctionComponent, useContext, useState} from 'react';
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Dropdown} from 'semantic-ui-react';

import {useCreateProjectMembersMutation} from '../generated/graphql';
import {PROJECT_MEMBERS_QUERY} from '../graphql/projects';
import {
  PROJECT_PAGE_QUERY,
  ProjectData,
} from '../state/graphql/projectPageQuery';
import {useViewer} from '../state/viewer/hooks';
import {
  EntityProjectPermissions,
  useViewerHasPermissionForEntityProject,
} from '../util/entityProjectPermissions';
import {
  doNotRetryContext,
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../util/errors';
import history from '../util/history';
import {AccessOption, getProjectAccess} from '../util/permissions';
import {useRampFlagStarredProjects} from '../util/rampFeatureFlags';
import {
  useTeamAdminIsNotRestrictedProjectMember,
  useViewerHasRestrictedProjectView,
} from '../util/restrictedProjectView';
import {StarredProjectsContext} from '../util/StarredProjectsContextProvider';
import * as urls from '../util/urls';
import {useNavUpdaterContext} from './NavContextProvider';
import {PROJECTS_BY_TEAM_QUERY} from './PaginatedList/projectQuery.query';
import {DeleteProjectModal} from './Project/DeleteProjectModal';
import {UndeleteRunsModal} from './UndeleteRunsModal';

export interface ExtraProjectActionsProps {
  project: ProjectData;
  isWeaveOnly?: boolean;
}

export const ExtraProjectActions: FunctionComponent<
  ExtraProjectActionsProps
> = ({project, isWeaveOnly}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUndeleteModalOpen, setIsUndeleteModalOpen] = useState(false);

  const viewer = useViewer();
  const {toggleEditProjectDrawer} = useNavUpdaterContext();

  const isStarredProjectsEnabled = useRampFlagStarredProjects();
  const {
    starredProjects,
    onStarClick,
    refetch: refetchStarredProjects,
  } = useContext(StarredProjectsContext);
  const isStarred = starredProjects[project.id] !== undefined;

  const [createProjectMembersMutation] = useCreateProjectMembersMutation({
    context: propagateErrorsContext(),
    ...doNotRetryContext(),
    refetchQueries: [
      // team admin has added themselves as a project member, so refetch the project data
      {
        query: PROJECTS_BY_TEAM_QUERY,
        variables: {entityName: project.entityName, first: 20},
      },
      {
        query: PROJECT_PAGE_QUERY,
        variables: {entityName: project.entityName, projectName: project.name},
      },
      {
        query: PROJECT_MEMBERS_QUERY,
        variables: {entityName: project.entityName, projectName: project.name},
      },
    ],
  });

  const projectAccess = getProjectAccess(project.access);

  const viewingRestricted = useViewerHasRestrictedProjectView({
    projectAccess,
    projectName: project.name,
    entityName: project.entityName,
  });

  const teamAdminIsNotRestrictedProjectMember =
    useTeamAdminIsNotRestrictedProjectMember({
      projectAccess,
      projectName: project.name,
      entityName: project.entityName,
    });

  const onJoinProjectClick = async () => {
    if (!viewer?.id) {
      return;
    }
    try {
      const res = await createProjectMembersMutation({
        variables: {
          userIds: [viewer.id],
          projectId: project.id,
        },
      });
      if (!res.data?.createProjectMembers?.success) {
        toast('Error joining project');
        return;
      }
    } catch (err) {
      const errMsg = extractErrorMessageFromApolloError(err);
      if (errMsg != null) {
        toast(`Error joining project: ${errMsg}`);
      } else {
        toast('Error joining project');
      }
    }
  };

  const {hasPermission: canDeleteProject} =
    useViewerHasPermissionForEntityProject(
      project.name,
      project.entityName,
      EntityProjectPermissions.DeleteProject
    );

  const {hasPermission: canUpdateProject} =
    useViewerHasPermissionForEntityProject(
      project.name,
      project.entityName,
      EntityProjectPermissions.UpdateProject
    );

  const {hasPermission: canRenameProject} =
    useViewerHasPermissionForEntityProject(
      project.name,
      project.entityName,
      EntityProjectPermissions.RenameProject
    );

  const viewerIsProjectOwner = viewer && project.user?.id === viewer?.id;
  const editEnabled =
    !viewingRestricted &&
    (canRenameProject ||
      canUpdateProject ||
      (!project.entity.isTeam && viewerIsProjectOwner));

  return (
    <Tailwind>
      <div className="flex items-center">
        {teamAdminIsNotRestrictedProjectMember && (
          <Button
            icon="add-new"
            className="mr-8"
            size="medium"
            variant="primary"
            onClick={() => onJoinProjectClick()}>
            Join project
          </Button>
        )}
        <TooltipDeprecated
          disabled={editEnabled}
          position="bottom center"
          size="mini"
          trigger={
            <span>
              <Button
                disabled={!editEnabled}
                className="mr-8"
                icon="pencil-edit"
                size="medium"
                variant="ghost"
                data-test="edit-project-details"
                onClick={toggleEditProjectDrawer}>
                Edit
              </Button>
            </span>
          }>
          {projectAccess === AccessOption.Restricted
            ? 'Only project admins can edit project details'
            : 'Only team and project admins can edit project details'}
        </TooltipDeprecated>
        {isStarredProjectsEnabled && (
          <Button
            className="mr-8"
            data-test={isStarred ? 'project-star-filled' : 'project-star-empty'}
            size="medium"
            variant="ghost"
            icon={isStarred ? 'star-filled' : 'star'}
            aria-label={isStarred ? 'Unstar project' : 'Star project'}
            onClick={() => {
              onStarClick(
                // Actual project data is not used, but createdAt is required so use lastActive as a placeholder
                {...project, createdAt: project.lastActive},
                !isStarred,
                'project overview'
              );
            }}
          />
        )}
        <Dropdown
          className="dropdown-menu-icon-button"
          floating
          trigger={
            <Button size="medium" icon="overflow-horizontal" variant="ghost" />
          }
          disabled={viewingRestricted}
          direction="left">
          <Dropdown.Menu>
            <Dropdown.Item
              icon="trash"
              content={
                <span>
                  Delete
                  {!canDeleteProject && <i> (admin only)</i>}
                </span>
              }
              onClick={() => setIsDeleteModalOpen(true)}
              disabled={!canDeleteProject}
            />
            {!isWeaveOnly && (
              <Dropdown.Item
                icon="refresh"
                content="Undelete recently deleted runs"
                onClick={() => setIsUndeleteModalOpen(true)}
              />
            )}
          </Dropdown.Menu>
        </Dropdown>
        <DeleteProjectModal
          isOpen={isDeleteModalOpen}
          project={project}
          setIsModalOpen={setIsDeleteModalOpen}
          handleModalClose={() => {
            setIsDeleteModalOpen(false);
            refetchStarredProjects();
            history.push(urls.allProjects(project.entityName));
          }}
        />
        <UndeleteRunsModal
          open={isUndeleteModalOpen}
          project={project}
          onClose={() => setIsUndeleteModalOpen(false)}
        />
      </div>
    </Tailwind>
  );
};
